/**
 * /portal/settings (T-046, PR-25, PR-26, GTM-12, GTM-14, GTM-18, US-11).
 *
 * Decision 2026-05-19 (supersedes the original DES-07 in-app pause/cancel
 * spec): ALL billing actions — payment method, plan change, pause, and
 * cancellation — are handled in the Stripe Customer Portal. There is no
 * custom in-app pause/cancel flow and no DES-07 locked pause/cancel copy
 * here anymore. Stripe is the billing source of truth (ARCH-001 §7); the
 * webhook (T-029) mirrors the resulting state back to subscribers.status
 * within seconds, and subscription-state.ts already maps Stripe
 * pause_collection -> status='paused'. Pause is enabled as a Stripe Customer
 * Portal configuration option (operator setup, see docs/stripe-setup.md), not
 * application code. This removes the migration-0015 set_subscription_state
 * pause/cancel path and the paused_until divergence entirely.
 *
 * What remains in-app on this page:
 *   - Tier display.
 *   - One "Manage subscription" button -> Stripe Customer Portal (reuses the
 *     existing T-030 /api/billing/portal endpoint).
 *   - Email-frequency control (a preference, not billing) -> still routes
 *     through /api/settings/email-frequency (set_subscription_state
 *     'email_frequency').
 *
 * Account deletion / GDPR erasure (PR-25b, DATA-16, DES-07b; ARCH-014) IS on
 * this page, as a separate section visually distinct from "Manage
 * subscription": deletion is a studio-side erasure, not a billing action, and
 * conflating the two risks an accidental irreversible wipe from a billing
 * click (ARCH-014 Alternatives). It requires an explicit typed DELETE
 * confirmation and states irreversibility plainly before the action is armed.
 */

import { useEffect, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import type { EmailFrequency, Subscriber } from '@studio/api-schemas';
import { getCurrentSession } from '@/lib/auth';
import {
  deleteAccount,
  fetchOwnSubscriber,
  updateEmailFrequency,
} from '@/lib/portal';
import { AsyncPanel, type AsyncState } from '@/components/portal/AsyncPanel';
import { toast } from '@/components/ui/toast';

export const Route = createFileRoute('/portal/settings')({
  component: SettingsPage,
});

function SettingsPage() {
  const [state, setState] = useState<AsyncState<Subscriber | null>>({
    status: 'loading',
  });

  useEffect(() => {
    let cancelled = false;
    fetchOwnSubscriber()
      .then((s) => {
        if (!cancelled) setState({ status: 'ready', data: s });
      })
      .catch(() => {
        if (!cancelled)
          setState({
            status: 'error',
            message: 'Could not load your settings. Try again in a moment.',
          });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="max-w-2xl space-y-10">
      <div>
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-[color:var(--color-text-mute)]">
          Account
        </p>
        <h1 className="mb-1 font-display text-h2 italic leading-tight text-[color:var(--color-navy)]">
          Settings.
        </h1>
        <p className="text-sm text-[color:var(--color-text-mute)]">
          Your plan, billing, and how often I email you.
        </p>
      </div>

      <AsyncPanel state={state} loadingLabel="Loading your settings...">
        {(sub) =>
          sub === null ? (
            <p className="text-sm text-[color:var(--color-text-mute)]">
              You are viewing as an operator; subscriber settings do not apply.
            </p>
          ) : (
            <div className="space-y-10">
              <PlanSection sub={sub} />
              <EmailFrequencySection sub={sub} />
              <DeleteAccountSection />
            </div>
          )
        }
      </AsyncPanel>
    </div>
  );
}

function PlanSection({ sub }: { sub: Subscriber }) {
  const [opening, setOpening] = useState(false);

  async function openBilling() {
    setOpening(true);
    try {
      const session = await getCurrentSession();
      if (!session) {
        toast.error('Your session expired. Sign in again.');
        setOpening(false);
        return;
      }
      const res = await fetch('/api/billing/portal', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
      });
      const json: unknown = await res.json();
      if (
        res.status === 200 &&
        typeof json === 'object' &&
        json !== null &&
        'url' in json &&
        typeof (json as { url: unknown }).url === 'string'
      ) {
        window.location.assign((json as { url: string }).url);
        return;
      }
      toast.error('Could not open the billing portal. Try again in a moment.');
    } catch {
      toast.error('Could not reach the billing service. Try again.');
    }
    setOpening(false);
  }

  return (
    <section>
      <h2 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-[color:var(--color-text-mute)]">
        Subscription
      </h2>
      <div className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-white)] p-5 sm:p-6">
        <div className="mb-3 flex items-center gap-2">
          <span className="inline-flex items-center rounded-sm bg-[color:var(--color-navy)] px-2 py-0.5 text-[10px] font-medium uppercase leading-none tracking-[0.05em] text-[color:var(--color-linen)]">
            {sub.tier === 'pro' ? 'Studio Pro' : 'Studio'}
          </span>
          <span className="inline-flex items-center gap-1.5 text-xs text-[color:var(--color-green-accent)]">
            <span
              className="h-1.5 w-1.5 rounded-full bg-[color:var(--color-green-accent)]"
              aria-hidden="true"
            />
            Active
          </span>
        </div>
        <p className="mb-4 text-sm leading-relaxed text-[color:var(--color-text-mute)]">
          Payment method, plan changes, pausing, and cancellation are all
          handled in Stripe&apos;s secure billing portal. Moving up takes
          effect right away; moving down, pausing, or cancelling takes effect
          at the end of the current period, so nothing in flight gets cut
          short. Pausing stops billing and keeps your account, context doc,
          playbook, and backlog exactly as-is; you can resume any time from the
          same portal.
        </p>
        <button
          type="button"
          onClick={openBilling}
          disabled={opening}
          className="inline-flex items-center justify-center rounded-md border-[1.5px] border-[color:var(--color-line-dark)] bg-transparent px-4 py-2 text-sm font-medium text-[color:var(--color-navy)] transition-colors hover:bg-[color:var(--color-linen)] disabled:opacity-50"
        >
          {opening ? 'Opening...' : 'Manage subscription'}
        </button>
      </div>
    </section>
  );
}

function EmailFrequencySection({ sub }: { sub: Subscriber }) {
  const [value, setValue] = useState<EmailFrequency>(sub.email_frequency);
  const [saving, setSaving] = useState(false);

  // Copy per LEARNINGS-PROPOSAL-BATCH-8 (OQ-E1): daily/weekly are window-
  // batched, NOT a single rolled-up summary email, so the labels say "batch"
  // rather than "summary" to match what actually sends.
  const OPTIONS: ReadonlyArray<{
    v: EmailFrequency;
    label: string;
    hint?: string;
  }> = [
    { v: 'immediate', label: 'As things happen' },
    {
      v: 'daily',
      label: 'Once a day',
      hint: 'We hold your updates and send them in a daily batch.',
    },
    {
      v: 'weekly',
      label: 'Once a week',
      hint: 'We hold your updates and send them in a weekly batch.',
    },
    { v: 'off', label: 'No emails' },
  ];

  async function save(next: EmailFrequency) {
    setValue(next);
    setSaving(true);
    const session = await getCurrentSession();
    if (!session) {
      toast.error('Your session expired. Sign in again.');
      setSaving(false);
      return;
    }
    const result = await updateEmailFrequency(session.access_token, next);
    if (result.ok) {
      toast.success('Email preference saved.');
    } else {
      toast.error(result.message);
      setValue(sub.email_frequency);
    }
    setSaving(false);
  }

  return (
    <section>
      <h2 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-[color:var(--color-text-mute)]">
        Notifications
      </h2>
      <p className="mb-3 text-sm text-[color:var(--color-text-mute)]">
        How often should I email you about your tasks?
      </p>
      <div className="grid gap-2 sm:grid-cols-2">
        {OPTIONS.map((o) => (
          <label
            key={o.v}
            className={[
              'cursor-pointer rounded-md border-[1.5px] p-3 text-sm transition-colors',
              value === o.v
                ? 'border-[color:var(--color-navy)] bg-[color:var(--color-rose-subtle2)]'
                : 'border-[color:var(--color-line-dark)] bg-[color:var(--color-white)] hover:bg-[color:var(--color-linen)]',
            ].join(' ')}
          >
            <input
              type="radio"
              name="email-frequency"
              className="sr-only"
              checked={value === o.v}
              disabled={saving}
              onChange={() => save(o.v)}
            />
            <span className="block text-[color:var(--color-navy)]">
              {o.label}
            </span>
            {o.hint ? (
              <span className="mt-1 block text-xs text-[color:var(--color-text-mute)]">
                {o.hint}
              </span>
            ) : null}
          </label>
        ))}
      </div>
    </section>
  );
}

/**
 * GDPR account deletion (ARCH-014). Deliberately the last section and styled
 * distinctly from "Manage subscription" so a billing click cannot become an
 * accidental erasure. The action is armed only when the subscriber types
 * DELETE exactly. Copy is in Eric's client-communication register: warm,
 * direct, honest about irreversibility. No em dashes, no banned phrases.
 */
function DeleteAccountSection() {
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [working, setWorking] = useState(false);
  const [done, setDone] = useState(false);

  const armed = confirmText === 'DELETE';

  async function runDelete() {
    if (!armed) return;
    setWorking(true);
    const session = await getCurrentSession();
    if (!session) {
      toast.error('Your session expired. Sign in again.');
      setWorking(false);
      return;
    }
    const result = await deleteAccount(session.access_token);
    setWorking(false);
    switch (result.kind) {
      case 'deleted':
      case 'already_deleted':
        setDone(true);
        toast.success(
          'Your account is deleted. Your backup is in your email inbox.',
        );
        return;
      case 'migration_pending':
      case 'backup_failed':
      case 'dry_run':
      case 'error':
        toast.error(result.message);
        return;
    }
  }

  if (done) {
    return (
      <section className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-white)] p-5">
        <h2 className="mb-2 font-display text-h4 italic text-[color:var(--color-navy)]">
          Account deleted
        </h2>
        <p className="text-sm text-[color:var(--color-text-mute)]">
          Your account and your strategic content are erased. A copy of your
          delivered work is in your email inbox. Billing and invoice records
          are kept only as long as Norwegian bookkeeping law requires. You can
          close this tab.
        </p>
      </section>
    );
  }

  return (
    <section className="rounded-lg border border-[color:var(--color-rose-dark)] bg-[color:var(--color-rose-subtle2)] p-5">
      <h2 className="mb-2 font-display text-h4 italic text-[color:var(--color-navy)]">
        Delete your account
      </h2>
      <p className="mb-3 text-sm text-[color:var(--color-navy)]">
        This is separate from cancelling. Cancelling stops billing and keeps
        everything. Deleting erases your context doc, notes, playbook, tasks,
        and deliverables, and removes your personal data. It cannot be undone.
      </p>
      <p className="mb-4 text-sm text-[color:var(--color-text-mute)]">
        Before anything is erased, I email you a copy of every deliverable so
        you keep your work. Billing and invoice records are kept only as long
        as Norwegian bookkeeping law requires, and nothing else.
      </p>

      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center justify-center rounded-md border-[1.5px] border-[color:var(--color-rose-dark)] bg-transparent px-4 py-2 text-sm font-medium text-[color:var(--color-navy)] transition-colors hover:bg-[color:var(--color-rose)]/30"
        >
          Delete my account
        </button>
      ) : (
        <div className="space-y-3">
          <label className="block text-sm text-[color:var(--color-navy)]">
            Type{' '}
            <span className="font-semibold">DELETE</span> to confirm. This is
            irreversible.
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              disabled={working}
              autoComplete="off"
              spellCheck={false}
              className="mt-2 block w-full rounded-md border border-[color:var(--color-line-dark)] bg-[color:var(--color-white)] px-3 py-2 text-sm text-[color:var(--color-navy)] transition-colors focus:border-[color:var(--color-navy)] focus:outline-none focus:ring-2 focus:ring-[color:var(--color-rose)]"
              placeholder="DELETE"
            />
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={runDelete}
              disabled={!armed || working}
              className="inline-flex items-center justify-center rounded-md border-[1.5px] border-[color:var(--color-rose-dark)] bg-[color:var(--color-rose)] px-4 py-2 text-sm font-medium text-[color:var(--color-navy)] transition-colors hover:bg-[color:var(--color-rose-dark)] disabled:opacity-50"
            >
              {working ? 'Deleting...' : 'Permanently delete'}
            </button>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setConfirmText('');
              }}
              disabled={working}
              className="inline-flex items-center justify-center rounded-md border-[1.5px] border-[color:var(--color-line-dark)] bg-[color:var(--color-white)] px-4 py-2 text-sm font-medium text-[color:var(--color-navy)] transition-colors hover:bg-[color:var(--color-linen)] disabled:opacity-50"
            >
              Keep my account
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
